/**
 * <AdminDashboardTab />
 *
 * Phase 25 — "what needs my attention this week" landing view. Fetches
 * GET /api/admin/dashboard/summary once on load (7 reports + an
 * `errors` map for any report whose query failed), then renders 7
 * independently-failing cards. Each card's "Refresh" button re-fetches
 * only that card's own endpoint -- not the whole summary -- so Carlton
 * can act on something and see just that number update.
 *
 * Layout: 2-col responsive grid (1-col below `md`, i.e. at the 375px
 * breakpoint). The Demand card spans both columns and gets a distinct,
 * forward-looking framing per the task brief -- it's the one report
 * that isn't an alert about something broken.
 */
import * as React from "react";
import {
  AlertCircle,
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  ChevronDown,
  ChevronUp,
  Lightbulb,
  RefreshCcw,
  Sparkles,
} from "lucide-react";
import { api, extractApiError } from "../../../lib/api";
import { Badge } from "../../../components/ui/Badge";
import { Button } from "../../../components/ui/Button";
import { Card, CardBody, CardHeader } from "../../../components/ui/Card";
import { Select } from "../../../components/ui/Select";

// ---------------------------------------------------------------------------
// Response shapes (mirror backend/routers/admin_dashboard.py)
// ---------------------------------------------------------------------------
interface WeeklyPoint {
  week_start: string;
  revenue_cents: number;
}
interface RevenueReport {
  period: "week" | "month";
  total_revenue_cents: number;
  participant_commission_cents: number;
  dealer_commission_cents: number;
  trend_vs_prior_period_pct: number;
  weekly_series: WeeklyPoint[];
}

interface StaleOrder {
  order_id: string;
  product_title: string;
  seller_username?: string;
  buyer_username?: string;
  paid_at?: string;
  shipped_at?: string;
  hours_stuck: number;
}
interface FulfillmentReport {
  counts_by_status: Record<string, number>;
  avg_hours_paid_to_shipped: number | null;
  avg_hours_shipped_to_delivered: number | null;
  stale_paid_orders: StaleOrder[];
  stale_shipped_orders: StaleOrder[];
}

interface StaleListing {
  product_id: string;
  title: string;
  seller_username: string;
  created_at: string;
  days_active: number;
  has_questions: boolean;
  has_fitment: boolean;
}
interface InventoryReport {
  counts_by_status: Record<string, number>;
  listings_no_questions_30d: number;
  listings_missing_fitment: number;
  stale_active_listings: StaleListing[];
}

interface ZeroListingDealer {
  user_id: string;
  username: string;
  email: string;
  subscription_status: string;
  dealer_since?: string | null;
  active_listing_count: 0;
}
interface DealersReport {
  active_dealer_count: number;
  mrr_cents: number;
  churned_this_month_count: number | null;
  churned_this_month_note?: string | null;
  dealers_paying_zero_listings: ZeroListingDealer[];
}

interface UnansweredQuestion {
  question_id: string;
  product_id: string;
  product_title: string;
  seller_username: string;
  asker_username: string;
  question_text: string;
  asked_at: string;
  hours_waiting: number;
}
interface SlowSeller {
  seller_username: string;
  avg_hours: number | null;
  unanswered_count: number;
}
interface QAReport {
  unanswered_count: number;
  unanswered_over_48h: UnansweredQuestion[];
  avg_response_time_hours: number | null;
  sellers_with_slowest_response: SlowSeller[];
}

interface SellerNoReviews {
  user_id: string;
  username: string;
  delivered_order_count: number;
  review_count: 0;
}
interface TrustReport {
  new_reviews_this_period: number;
  platform_avg_rating: number | null;
  platform_avg_rating_trend: "up" | "down" | "flat" | null;
  sellers_delivered_no_reviews: SellerNoReviews[];
}

interface ZeroResultSearch {
  query: string;
  search_count: number;
  last_searched_at: string;
}
interface LowResultSearch {
  query: string;
  search_count: number;
  avg_result_count: number;
}
interface SavedSearch {
  query: string;
  save_count: number;
}
interface DemandReport {
  top_zero_result_searches: ZeroResultSearch[];
  top_low_result_searches: LowResultSearch[];
  top_saved_searches: SavedSearch[];
}

interface SummaryPayload {
  revenue?: RevenueReport;
  fulfillment?: FulfillmentReport;
  inventory?: InventoryReport;
  dealers?: DealersReport;
  qa_response?: QAReport;
  trust?: TrustReport;
  demand?: DemandReport;
  errors: Record<string, string>;
}

type ReportKey = "revenue" | "fulfillment" | "inventory" | "dealers" | "qa_response" | "trust" | "demand";

const ENDPOINTS: Record<ReportKey, string> = {
  revenue: "/admin/dashboard/revenue",
  fulfillment: "/admin/dashboard/fulfillment",
  inventory: "/admin/dashboard/inventory",
  dealers: "/admin/dashboard/dealers",
  qa_response: "/admin/dashboard/qa-response",
  trust: "/admin/dashboard/trust",
  demand: "/admin/dashboard/demand",
};

// ---------------------------------------------------------------------------
// Small shared helpers
// ---------------------------------------------------------------------------
function fmtUsd(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

function fmtHours(h: number | null): string {
  if (h === null) return "—";
  if (h < 48) return `${h.toFixed(1)}h`;
  return `${(h / 24).toFixed(1)}d`;
}

function Sparkline({ values }: { values: number[] }) {
  if (values.length === 0) return null;
  const w = 140;
  const h = 36;
  const max = Math.max(1, ...values);
  const min = Math.min(0, ...values);
  const range = max - min || 1;
  const step = values.length > 1 ? w / (values.length - 1) : 0;
  const points = values.map((v, i) => `${i * step},${h - ((v - min) / range) * h}`).join(" ");
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="text-brand" aria-hidden="true">
      <polyline points={points} fill="none" stroke="currentColor" strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

function TrendPct({ pct }: { pct: number }) {
  if (pct > 0.05) {
    return (
      <span className="inline-flex items-center gap-0.5 text-emerald-700 text-sm font-semibold">
        <ArrowUp className="h-4 w-4" aria-hidden /> {pct.toFixed(1)}%
      </span>
    );
  }
  if (pct < -0.05) {
    return (
      <span className="inline-flex items-center gap-0.5 text-brand text-sm font-semibold">
        <ArrowDown className="h-4 w-4" aria-hidden /> {Math.abs(pct).toFixed(1)}%
      </span>
    );
  }
  return <span className="text-ink-muted text-sm font-semibold">Flat</span>;
}

/** Toggleable "View N details" list -- shared by 5 of the 7 cards. */
function ExpandableList<T>({
  items,
  testid,
  renderItem,
  emptyLabel,
  label,
}: {
  items: T[];
  testid: string;
  renderItem: (item: T, idx: number) => React.ReactNode;
  emptyLabel?: string;
  label: string;
}) {
  const [open, setOpen] = React.useState(false);
  if (items.length === 0) {
    return emptyLabel ? <p className="text-sm text-ink-muted">{emptyLabel}</p> : null;
  }
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid={`${testid}-toggle`}
        className="inline-flex items-center gap-1.5 h-12 px-1 text-sm font-semibold text-ink underline-offset-2 hover:underline"
      >
        {open ? <ChevronUp className="h-4 w-4" aria-hidden /> : <ChevronDown className="h-4 w-4" aria-hidden />}
        {open ? "Hide" : label}
      </button>
      {open && (
        <ul
          data-testid={testid}
          className="mt-1 flex flex-col divide-y divide-surface-border rounded-xl border border-surface-border bg-surface-raised"
        >
          {items.map((item, idx) => (
            <li key={idx} className="px-3 py-2.5 text-sm">
              {renderItem(item, idx)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Chrome shared by every report card: header + refresh + loading/error. */
function ReportCard({
  title,
  icon,
  loading,
  error,
  onRefresh,
  testid,
  highlight,
  className,
  children,
}: {
  title: string;
  icon: React.ReactNode;
  loading: boolean;
  error: string | null;
  onRefresh: () => void;
  testid: string;
  highlight?: boolean;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <Card
      elevated
      data-testid={testid}
      className={`flex flex-col ${highlight ? "border-2 border-ink" : ""} ${className ?? ""}`}
    >
      <CardHeader>
        <h3 className="text-lg font-bold inline-flex items-center gap-2">
          {icon} {title}
        </h3>
        <button
          type="button"
          aria-label={`Refresh ${title}`}
          onClick={onRefresh}
          disabled={loading}
          data-testid={`${testid}-refresh`}
          className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-full border-2 border-surface-border text-ink-muted hover:border-ink hover:text-ink transition-colors disabled:opacity-50"
        >
          <RefreshCcw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} aria-hidden />
        </button>
      </CardHeader>
      <CardBody className="flex-1">
        {error ? (
          <div data-testid={`${testid}-error`} className="flex flex-col gap-2">
            <p className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand">
              <AlertCircle className="h-4 w-4" aria-hidden /> This report failed to load.
            </p>
            <p className="text-xs text-ink-muted">{error}</p>
            <Button variant="outline" size="md" onClick={onRefresh} data-testid={`${testid}-retry`}>
              Retry
            </Button>
          </div>
        ) : loading && !children ? (
          <div className="h-10 w-10 rounded-full border-4 border-surface-border border-t-brand animate-spin" />
        ) : (
          children
        )}
      </CardBody>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Main tab
// ---------------------------------------------------------------------------
export function AdminDashboardTab() {
  const [period, setPeriod] = React.useState<"week" | "month">("week");
  const [summary, setSummary] = React.useState<SummaryPayload | null>(null);
  const [summaryError, setSummaryError] = React.useState<string | null>(null);
  const [summaryLoading, setSummaryLoading] = React.useState(true);

  // Per-card overrides from an individual "Refresh" click, plus their
  // own loading/error state -- independent of the summary fetch.
  const [overrides, setOverrides] = React.useState<Partial<Record<ReportKey, unknown>>>({});
  const [cardLoading, setCardLoading] = React.useState<Partial<Record<ReportKey, boolean>>>({});
  const [cardErrors, setCardErrors] = React.useState<Partial<Record<ReportKey, string | null>>>({});

  const loadSummary = React.useCallback(async () => {
    setSummaryLoading(true);
    setSummaryError(null);
    try {
      const r = await api.get<SummaryPayload>("/admin/dashboard/summary", { params: { period } });
      setSummary(r.data);
      setOverrides({});
      setCardErrors({});
    } catch (err) {
      setSummaryError(extractApiError(err));
    } finally {
      setSummaryLoading(false);
    }
  }, [period]);

  React.useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const refreshCard = React.useCallback(
    async (key: ReportKey) => {
      setCardLoading((prev) => ({ ...prev, [key]: true }));
      setCardErrors((prev) => ({ ...prev, [key]: null }));
      try {
        const params = key === "revenue" || key === "trust" ? { period } : undefined;
        const r = await api.get(ENDPOINTS[key], { params });
        setOverrides((prev) => ({ ...prev, [key]: r.data }));
      } catch (err) {
        setCardErrors((prev) => ({ ...prev, [key]: extractApiError(err) }));
      } finally {
        setCardLoading((prev) => ({ ...prev, [key]: false }));
      }
    },
    [period],
  );

  const dataFor = <T,>(key: ReportKey): T | undefined =>
    (overrides[key] as T | undefined) ?? (summary?.[key] as T | undefined);
  const errorFor = (key: ReportKey): string | null =>
    cardErrors[key] ?? summary?.errors?.[key] ?? null;
  const loadingFor = (key: ReportKey): boolean => !!cardLoading[key] || (summaryLoading && !summary);

  if (summaryError && !summary) {
    return (
      <Card elevated data-testid="admin-dashboard-fatal-error">
        <CardBody className="text-center py-12">
          <AlertCircle className="h-8 w-8 text-brand mx-auto mb-3" aria-hidden />
          <p className="text-ink-muted mb-4">{summaryError}</p>
          <Button variant="primary" onClick={loadSummary} data-testid="admin-dashboard-retry-all">
            Retry
          </Button>
        </CardBody>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-4" data-testid="admin-dashboard-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-black tracking-tight">Dashboard</h2>
          <p className="text-ink-muted text-sm mt-1">What needs your attention this week.</p>
        </div>
        <div className="w-40">
          <Select
            label="Period"
            hideLabel
            value={period}
            onChange={(e) => setPeriod(e.target.value as "week" | "month")}
            options={[
              { value: "week", label: "This week" },
              { value: "month", label: "This month" },
            ]}
            data-testid="admin-dashboard-period"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <RevenueCard
          data={dataFor<RevenueReport>("revenue")}
          loading={loadingFor("revenue")}
          error={errorFor("revenue")}
          onRefresh={() => refreshCard("revenue")}
        />
        <FulfillmentCard
          data={dataFor<FulfillmentReport>("fulfillment")}
          loading={loadingFor("fulfillment")}
          error={errorFor("fulfillment")}
          onRefresh={() => refreshCard("fulfillment")}
        />
        <InventoryCard
          data={dataFor<InventoryReport>("inventory")}
          loading={loadingFor("inventory")}
          error={errorFor("inventory")}
          onRefresh={() => refreshCard("inventory")}
        />
        <DealersCard
          data={dataFor<DealersReport>("dealers")}
          loading={loadingFor("dealers")}
          error={errorFor("dealers")}
          onRefresh={() => refreshCard("dealers")}
        />
        <QACard
          data={dataFor<QAReport>("qa_response")}
          loading={loadingFor("qa_response")}
          error={errorFor("qa_response")}
          onRefresh={() => refreshCard("qa_response")}
        />
        <TrustCard
          data={dataFor<TrustReport>("trust")}
          loading={loadingFor("trust")}
          error={errorFor("trust")}
          onRefresh={() => refreshCard("trust")}
        />
        <DemandCard
          data={dataFor<DemandReport>("demand")}
          loading={loadingFor("demand")}
          error={errorFor("demand")}
          onRefresh={() => refreshCard("demand")}
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 1. Revenue
// ---------------------------------------------------------------------------
function RevenueCard({
  data,
  loading,
  error,
  onRefresh,
}: {
  data?: RevenueReport;
  loading: boolean;
  error: string | null;
  onRefresh: () => void;
}) {
  return (
    <ReportCard
      title="Revenue"
      icon={<Sparkles className="h-5 w-5" aria-hidden />}
      loading={loading}
      error={error}
      onRefresh={onRefresh}
      testid="dash-revenue-card"
    >
      {data && (
        <div className="flex flex-col gap-3">
          <div className="flex items-end justify-between gap-3 flex-wrap">
            <div>
              <div className="text-3xl font-black font-display" data-testid="dash-revenue-total">
                {fmtUsd(data.total_revenue_cents)}
              </div>
              <div className="text-xs text-ink-muted uppercase tracking-wider mt-0.5">
                this {data.period} · <TrendPct pct={data.trend_vs_prior_period_pct} /> vs prior {data.period}
              </div>
            </div>
            <Sparkline values={data.weekly_series.map((w) => w.revenue_cents)} />
          </div>
          <div className="text-sm text-ink-muted flex gap-4">
            <span>
              Participant commission <strong className="text-ink">{fmtUsd(data.participant_commission_cents)}</strong>
            </span>
            <span>
              Dealer commission <strong className="text-ink">{fmtUsd(data.dealer_commission_cents)}</strong>
            </span>
          </div>
        </div>
      )}
    </ReportCard>
  );
}

// ---------------------------------------------------------------------------
// 2. Fulfillment
// ---------------------------------------------------------------------------
function FulfillmentCard({
  data,
  loading,
  error,
  onRefresh,
}: {
  data?: FulfillmentReport;
  loading: boolean;
  error: string | null;
  onRefresh: () => void;
}) {
  return (
    <ReportCard
      title="Fulfillment"
      icon={<AlertTriangle className="h-5 w-5" aria-hidden />}
      loading={loading}
      error={error}
      onRefresh={onRefresh}
      testid="dash-fulfillment-card"
    >
      {data && (
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap gap-3 text-sm">
            {Object.entries(data.counts_by_status).map(([status, count]) => (
              <span key={status} className="inline-flex items-center gap-1">
                <Badge variant="muted" size="sm">
                  {status}
                </Badge>
                <strong>{count}</strong>
              </span>
            ))}
          </div>
          <div className="text-xs text-ink-muted">
            Avg paid→shipped {fmtHours(data.avg_hours_paid_to_shipped)} · Avg shipped→delivered{" "}
            {fmtHours(data.avg_hours_shipped_to_delivered)}
          </div>

          {data.stale_paid_orders.length > 0 && (
            <p className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand" data-testid="dash-fulfillment-stale-paid-alert">
              <AlertCircle className="h-4 w-4" aria-hidden /> {data.stale_paid_orders.length} order
              {data.stale_paid_orders.length === 1 ? "" : "s"} stuck &gt;72h unshipped
            </p>
          )}
          <ExpandableList
            items={data.stale_paid_orders}
            testid="dash-fulfillment-stale-paid-list"
            label={`View ${data.stale_paid_orders.length} stuck order${data.stale_paid_orders.length === 1 ? "" : "s"}`}
            renderItem={(o) => (
              <div>
                <span className="font-semibold">{o.product_title}</span> · @{o.seller_username} ·{" "}
                <span className="text-brand font-semibold">{fmtHours(o.hours_stuck)} stuck</span>
              </div>
            )}
          />

          {/* Yellow (not the brand red/black palette) is the one accepted
              status-indicator exception established in Phase 14-A's
              EmailStatusPill -- reused here for "needs eyes, not urgent". */}
          {data.stale_shipped_orders.length > 0 && (
            <p
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-amber-700"
              data-testid="dash-fulfillment-stale-shipped-alert"
            >
              <AlertTriangle className="h-4 w-4" aria-hidden /> {data.stale_shipped_orders.length} order
              {data.stale_shipped_orders.length === 1 ? "" : "s"} awaiting confirmation &gt;30d
            </p>
          )}
          <ExpandableList
            items={data.stale_shipped_orders}
            testid="dash-fulfillment-stale-shipped-list"
            label={`View ${data.stale_shipped_orders.length} awaiting confirmation`}
            renderItem={(o) => (
              <div>
                <span className="font-semibold">{o.product_title}</span> · @{o.buyer_username} ·{" "}
                <span className="text-amber-700 font-semibold">{fmtHours(o.hours_stuck)} waiting</span>
              </div>
            )}
          />
        </div>
      )}
    </ReportCard>
  );
}

// ---------------------------------------------------------------------------
// 3. Inventory
// ---------------------------------------------------------------------------
function InventoryCard({
  data,
  loading,
  error,
  onRefresh,
}: {
  data?: InventoryReport;
  loading: boolean;
  error: string | null;
  onRefresh: () => void;
}) {
  return (
    <ReportCard
      title="Inventory"
      icon={<Sparkles className="h-5 w-5" aria-hidden />}
      loading={loading}
      error={error}
      onRefresh={onRefresh}
      testid="dash-inventory-card"
    >
      {data && (
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap gap-3 text-sm">
            {Object.entries(data.counts_by_status).map(([status, count]) => (
              <span key={status} className="inline-flex items-center gap-1">
                <Badge variant="muted" size="sm">
                  {status}
                </Badge>
                <strong>{count}</strong>
              </span>
            ))}
          </div>

          <p className="text-sm" data-testid="dash-inventory-no-questions">
            <strong>{data.listings_no_questions_30d}</strong> listings haven&apos;t gotten a question in 30
            days — good candidates for a seller promo nudge.
          </p>
          <p className="text-sm" data-testid="dash-inventory-missing-fitment">
            <strong>{data.listings_missing_fitment}</strong> listings are missing fitment data — an easy
            upsell email to send sellers.
          </p>

          <ExpandableList
            items={data.stale_active_listings}
            testid="dash-inventory-stale-list"
            label={`View ${data.stale_active_listings.length} listing${data.stale_active_listings.length === 1 ? "" : "s"} active &gt;60d`}
            renderItem={(l) => (
              <div>
                <span className="font-semibold">{l.title}</span> · @{l.seller_username} ·{" "}
                {l.days_active.toFixed(0)}d active
                {!l.has_fitment && <span className="text-ink-muted"> · no fitment</span>}
                {!l.has_questions && <span className="text-ink-muted"> · no questions</span>}
              </div>
            )}
          />
        </div>
      )}
    </ReportCard>
  );
}

// ---------------------------------------------------------------------------
// 4. Dealers
// ---------------------------------------------------------------------------
function DealersCard({
  data,
  loading,
  error,
  onRefresh,
}: {
  data?: DealersReport;
  loading: boolean;
  error: string | null;
  onRefresh: () => void;
}) {
  return (
    <ReportCard
      title="Dealers"
      icon={<Sparkles className="h-5 w-5" aria-hidden />}
      loading={loading}
      error={error}
      onRefresh={onRefresh}
      testid="dash-dealers-card"
    >
      {data && (
        <div className="flex flex-col gap-3">
          <div className="flex items-end gap-6">
            <div>
              <div className="text-3xl font-black font-display" data-testid="dash-dealers-count">
                {data.active_dealer_count}
              </div>
              <div className="text-xs text-ink-muted uppercase tracking-wider">Active dealers</div>
            </div>
            <div>
              <div className="text-3xl font-black font-display" data-testid="dash-dealers-mrr">
                {fmtUsd(data.mrr_cents)}
              </div>
              <div className="text-xs text-ink-muted uppercase tracking-wider">MRR</div>
            </div>
          </div>

          {data.churned_this_month_count === null ? (
            <p className="text-xs text-ink-muted" data-testid="dash-dealers-churn-note">
              Churn this month: not trackable yet.
            </p>
          ) : (
            <p className="text-sm">
              <strong>{data.churned_this_month_count}</strong> churned this month
            </p>
          )}

          {data.dealers_paying_zero_listings.length > 0 && (
            <p className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand" data-testid="dash-dealers-zero-listings-alert">
              <AlertCircle className="h-4 w-4" aria-hidden /> {data.dealers_paying_zero_listings.length} paying
              dealer{data.dealers_paying_zero_listings.length === 1 ? "" : "s"} with zero active listings
            </p>
          )}
          <ExpandableList
            items={data.dealers_paying_zero_listings}
            testid="dash-dealers-zero-listings-list"
            label={`View ${data.dealers_paying_zero_listings.length} dealer${data.dealers_paying_zero_listings.length === 1 ? "" : "s"}`}
            renderItem={(d) => (
              <div>
                <span className="font-semibold">@{d.username}</span> ·{" "}
                <a href={`mailto:${d.email}`} className="underline">
                  {d.email}
                </a>
              </div>
            )}
          />
        </div>
      )}
    </ReportCard>
  );
}

// ---------------------------------------------------------------------------
// 5. Q&A response time
// ---------------------------------------------------------------------------
function QACard({
  data,
  loading,
  error,
  onRefresh,
}: {
  data?: QAReport;
  loading: boolean;
  error: string | null;
  onRefresh: () => void;
}) {
  return (
    <ReportCard
      title="Q&A response time"
      icon={<Sparkles className="h-5 w-5" aria-hidden />}
      loading={loading}
      error={error}
      onRefresh={onRefresh}
      testid="dash-qa-card"
    >
      {data && (
        <div className="flex flex-col gap-3">
          <div className="flex items-end gap-6">
            <div>
              <div className="text-3xl font-black font-display" data-testid="dash-qa-unanswered">
                {data.unanswered_count}
              </div>
              <div className="text-xs text-ink-muted uppercase tracking-wider">Unanswered</div>
            </div>
            <div>
              <div className="text-3xl font-black font-display">{fmtHours(data.avg_response_time_hours)}</div>
              <div className="text-xs text-ink-muted uppercase tracking-wider">Avg response</div>
            </div>
          </div>

          {data.unanswered_over_48h.length > 0 && (
            <p className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand" data-testid="dash-qa-48h-alert">
              <AlertCircle className="h-4 w-4" aria-hidden /> {data.unanswered_over_48h.length} question
              {data.unanswered_over_48h.length === 1 ? "" : "s"} waiting &gt;48h
            </p>
          )}
          <ExpandableList
            items={data.unanswered_over_48h}
            testid="dash-qa-48h-list"
            label={`View ${data.unanswered_over_48h.length} waiting question${data.unanswered_over_48h.length === 1 ? "" : "s"}`}
            renderItem={(q) => (
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="truncate">
                    <span className="font-semibold">{q.product_title}</span> · @{q.seller_username}
                  </div>
                  <div className="text-ink-muted text-xs truncate">{q.question_text}</div>
                  <div className="text-brand text-xs font-semibold">{fmtHours(q.hours_waiting)} waiting</div>
                </div>
                <a
                  href={`/product/${q.product_id}#questions`}
                  className="shrink-0 text-xs font-semibold underline whitespace-nowrap h-12 inline-flex items-center"
                  data-testid={`dash-qa-view-listing-${q.question_id}`}
                >
                  View listing →
                </a>
              </div>
            )}
          />

          {data.sellers_with_slowest_response.length > 0 && (
            <div data-testid="dash-qa-slowest-sellers">
              <p className="text-xs font-semibold uppercase tracking-wider text-ink-muted mb-1">
                Slowest responders
              </p>
              <ul className="text-sm flex flex-col gap-0.5">
                {data.sellers_with_slowest_response.map((s) => (
                  <li key={s.seller_username}>
                    @{s.seller_username} — {s.avg_hours === null ? "never answered" : `${fmtHours(s.avg_hours)} avg`}
                    {s.unanswered_count > 0 && ` (${s.unanswered_count} unanswered)`}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </ReportCard>
  );
}

// ---------------------------------------------------------------------------
// 6. Trust / reviews
// ---------------------------------------------------------------------------
function TrustCard({
  data,
  loading,
  error,
  onRefresh,
}: {
  data?: TrustReport;
  loading: boolean;
  error: string | null;
  onRefresh: () => void;
}) {
  return (
    <ReportCard
      title="Trust & reviews"
      icon={<Sparkles className="h-5 w-5" aria-hidden />}
      loading={loading}
      error={error}
      onRefresh={onRefresh}
      testid="dash-trust-card"
    >
      {data && (
        <div className="flex flex-col gap-3">
          <div className="flex items-end gap-6">
            <div>
              <div className="text-3xl font-black font-display" data-testid="dash-trust-new-reviews">
                {data.new_reviews_this_period}
              </div>
              <div className="text-xs text-ink-muted uppercase tracking-wider">New reviews</div>
            </div>
            <div>
              <div className="text-3xl font-black font-display inline-flex items-center gap-2">
                {data.platform_avg_rating === null ? "—" : data.platform_avg_rating.toFixed(1)}
                {data.platform_avg_rating_trend === "up" && <ArrowUp className="h-5 w-5 text-emerald-700" aria-hidden />}
                {data.platform_avg_rating_trend === "down" && <ArrowDown className="h-5 w-5 text-brand" aria-hidden />}
              </div>
              <div className="text-xs text-ink-muted uppercase tracking-wider">Avg rating</div>
            </div>
          </div>

          {data.sellers_delivered_no_reviews.length > 0 && (
            <p className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand" data-testid="dash-trust-no-reviews-alert">
              <AlertCircle className="h-4 w-4" aria-hidden /> {data.sellers_delivered_no_reviews.length} seller
              {data.sellers_delivered_no_reviews.length === 1 ? "" : "s"} delivered orders but have zero reviews
            </p>
          )}
          <ExpandableList
            items={data.sellers_delivered_no_reviews}
            testid="dash-trust-no-reviews-list"
            label={`View ${data.sellers_delivered_no_reviews.length} seller${data.sellers_delivered_no_reviews.length === 1 ? "" : "s"}`}
            renderItem={(s) => (
              <div>
                <span className="font-semibold">@{s.username}</span> · {s.delivered_order_count} delivered order
                {s.delivered_order_count === 1 ? "" : "s"}, 0 reviews
              </div>
            )}
          />
        </div>
      )}
    </ReportCard>
  );
}

// ---------------------------------------------------------------------------
// 7. Demand -- forward-looking, visually distinct (highlighted border,
// spans both grid columns) per the task brief.
// ---------------------------------------------------------------------------
function DemandCard({
  data,
  loading,
  error,
  onRefresh,
}: {
  data?: DemandReport;
  loading: boolean;
  error: string | null;
  onRefresh: () => void;
}) {
  return (
    <ReportCard
      title="What buyers are searching for that you don't have"
      icon={<Lightbulb className="h-5 w-5" aria-hidden />}
      loading={loading}
      error={error}
      onRefresh={onRefresh}
      testid="dash-demand-card"
      highlight
      className="md:col-span-2"
    >
      {data && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <p className="text-sm font-bold mb-2">Zero results</p>
            {data.top_zero_result_searches.length === 0 ? (
              <p className="text-sm text-ink-muted">No zero-result searches yet.</p>
            ) : (
              <ul className="flex flex-col gap-1.5 text-sm" data-testid="dash-demand-zero-list">
                {data.top_zero_result_searches.map((s) => (
                  <li key={s.query} className="flex items-center justify-between gap-2">
                    <span className="font-semibold truncate">&ldquo;{s.query}&rdquo;</span>
                    <Badge variant="brand" size="sm">
                      {s.search_count}×
                    </Badge>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div>
            <p className="text-sm font-bold mb-2">Low results (1-3)</p>
            {data.top_low_result_searches.length === 0 ? (
              <p className="text-sm text-ink-muted">No low-result searches yet.</p>
            ) : (
              <ul className="flex flex-col gap-1.5 text-sm" data-testid="dash-demand-low-list">
                {data.top_low_result_searches.map((s) => (
                  <li key={s.query} className="flex items-center justify-between gap-2">
                    <span className="truncate">&ldquo;{s.query}&rdquo;</span>
                    <span className="text-ink-muted text-xs shrink-0">
                      {s.search_count}× · avg {s.avg_result_count.toFixed(1)} results
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-ink-muted mb-2">Popular searches</p>
            {data.top_saved_searches.length === 0 ? (
              <p className="text-sm text-ink-muted">No saved searches yet.</p>
            ) : (
              <ul className="flex flex-wrap gap-1.5" data-testid="dash-demand-saved-list">
                {data.top_saved_searches.map((s) => (
                  <li key={s.query}>
                    <Badge variant="muted" size="sm">
                      {s.query} ({s.save_count})
                    </Badge>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </ReportCard>
  );
}
